import { faCircleSterling } from '@fortawesome/pro-light-svg-icons';
import * as Ramda from 'ramda';
import Numeral from 'numeral';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCurrencySymbolByLanguage, getRegionByProduct } from '../i18n/utils';
import { deriveCurrencyFormat } from '../i18n/currencies';
import type { ProductType } from '../types';
import type { Languages } from '../i18n/i18n.language-config';
import { languages } from '../i18n/i18n.language-config';
import CircleDollar from '../_assets/svg/CircleDollar.svg';
import { moneyFormat, moneyFormat2Decimals } from './appUtils';
import { getPlatformRegion } from '.';

export const LIMIT_CURRENCY_FORMAT = '0.00a';

export function safeRoundTo2Decimals(n: number) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export const parseNumberTo2Decimals = Ramda.compose<
  [string | number],
  string,
  number,
  string
>(Ramda.invoker(1, 'toFixed')(2), parseFloat, String);

export function numberToCurrency(value: number, format?: string) {
  if (!format) {
    return moneyFormat(value);
  }
  return Numeral(value).format(format);
}

export function currencyToNumber(value: string) {
  return Numeral(value).value();
}

type CurrencyType = 'float';

export function toCurrency(type: CurrencyType | undefined) {
  return (value: number) => {
    switch (type) {
      case 'float':
        return moneyFormat2Decimals(value);
      default:
        return moneyFormat(value);
    }
  };
}

const language = getPlatformRegion();
export const formatCurrencyToTenthsPlaceWithUnit = (value: string | number) =>
  regionCurrencyToTenthsPlace(language)(value);

export const regionCurrencyToTenthsPlace =
  (language: Languages) => (value: number | string) => {
    const symbol = getCurrencySymbolByLanguage(language);
    return `${symbol}${Numeral(value).format('0.0a')}`;
  };

export const noDecimalRegionCurrencyWithUnit = (value: string | number) => {
  const symbol = getCurrencySymbolByLanguage(language);
  return `${symbol}${Numeral(value).format('0a')}`;
};

export function formatCurrencyAsFloat(value: number | undefined) {
  // eslint-disable-next-line
  if (value == null || isNaN(value)) {
    return '-';
  }

  const parsedValue = Number(parseNumberTo2Decimals(value));
  if (parsedValue === 0) {
    return moneyFormat(parsedValue);
  }

  return moneyFormat2Decimals(parsedValue);
}

export const formatPropAsCurrency = (prop: string) => {
  return Ramda.compose(formatCurrencyAsFloat, Ramda.prop(prop));
};

type Options = Parameters<typeof deriveCurrencyFormat>[2];

export const formatTableCurrencyByRegion: <
  T extends { product?: ProductType; productType?: ProductType }
>(
  formatOptions?: Options
) => (path: string | Ramda.Path) => (row: T) => string | null =
  (formatOptions?: Options) => (path) => (row) => {
    if (!row) {
      return null;
    }

    const productType = row?.product || row?.productType;
    const language = getRegionByProduct(productType as ProductType);

    const _path: string[] = Array.isArray(path)
      ? path
      : (path as string).split('.');

    return deriveCurrencyFormat(
      language,
      Ramda.path(_path, row),
      formatOptions
    );
  };

export const StatBarCurrencySymbol = ({
  region,
  ...props
}: {
  region: Languages;
}) => {
  if (region === languages['en-GB']) {
    return (
      <FontAwesomeIcon
        style={{
          width: '3rem',
          height: '3rem',
          marginRight: '0.41666667rem',
          color: 'rgb(145, 146, 164)',
        }}
        icon={faCircleSterling}
        {...props}
      />
    );
  }

  return (
    <CircleDollar
      style={{ width: '3.333333rem', marginRight: '0.41666667rem' }}
      {...props}
    />
  );
};
